import { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import "../../Analysis/ComponentsAnalysis/AnalysisActions/AnalysisActions.css";
import AdequacaoModalConfirmacao from "./AdequacaoModalConfirmacao";
import NotificationToast from "../../../../components/NotificationToast/NotificationToast";

export interface ItemNaoConforme {
  id?: number;
  idCampoJson?: string;
  observacao?: string;
  selecionado?: boolean;
  [key: string]: unknown;
}

export interface SolicitacaoAdequacaoModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: (selecionados: ItemNaoConforme[]) => void;
  itensNaoConformes?: ItemNaoConforme[] | unknown[];
  titulosValor?: Record<string, string>;
  onOpenConfirmModal: (selecionados: ItemNaoConforme[]) => void;
}

const SolicitacaoAdequacaoModal = ({
  show,
  onClose,
  onSubmit,
  itensNaoConformes = [],
  titulosValor = {},
  onOpenConfirmModal,
}: SolicitacaoAdequacaoModalProps) => {
  const [itens, setItens] = useState<ItemNaoConforme[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    const carregarItens = async () => {
      setIsLoading(true);
      try {
        const arr = Array.isArray(itensNaoConformes) ? itensNaoConformes : [];

        const byCampo = new Map();

        const getKey = (it) =>
          it?.idCampoJson ?? it?.IdCampoJson ?? it?.id_campo_json ?? null;

        const parseDate = (s) => {
          if (!s || s === "NULL") return NaN;
          const str = String(s).trim();
          if (!str || /^0001-01-01/.test(str)) return NaN;
          const norm = str.includes("T") ? str : str.replace(" ", "T");
          const t = Date.parse(norm);
          return Number.isNaN(t) ? NaN : t;
        };

        const getTs = (it) => {
          const raw =
            it?.DataSolicitacao ??
            it?.dataSolicitacao ??
            it?.["dataSolicitação"] ??
            it?.DataCorrecao ??
            it?.dataCorrecao ??
            it?.createdAt ??
            it?.CriadoEm ??
            it?.criadoEm ??
            null;
          const t = parseDate(raw);
          if (!Number.isNaN(t)) return t;
          return Number(it?.Id ?? it?.id ?? 0);
        };

        for (const it of arr) {
          if (!it || typeof it !== "object") continue;
          const k = getKey(it);
          if (k == null) continue;
          const ts = getTs(it);
          const prev = byCampo.get(k);
          if (!prev || ts > prev.__ts) {
            byCampo.set(k, { ...(it as Record<string, unknown>), __ts: ts });
          }
        }

        const ultimosPorCampo = Array.from(byCampo.values()).map(
          ({ __ts, ...rest }) => rest
        );

        const filtrados = ultimosPorCampo.filter((item) => {
          if (!item) return false;
          if (item.conforme !== false) return false;

          const dataSol =
            item?.dataSolicitacao ??
            item?.["dataSolicitação"] ??
            item?.DataSolicitacao ??
            null;

          const isNullish =
            dataSol == null ||
            dataSol === "NULL" ||
            String(dataSol).trim() === "" ||
            /^0001-01-01/.test(String(dataSol));

          if (isNullish) return true;
          if (item.adequacaoRealizada === true) return true;
          return false;
        });

        const itensPreparados = filtrados.map((item) => ({
          ...item,
          observacao:
            item.observacao ??
            item.Observacao ??
            item.observacaoAnalista ??
            item.ObservacaoAnalista ??
            item.obsAnalista ??
            item.ObsAnalista ??
            "",
          selecionado: item.selecionado ?? false,
        }));

        setItens(itensPreparados);
      } finally {
        setIsLoading(false);
      }
    };

    if (show) carregarItens();
  }, [show, itensNaoConformes]);


  const handleChange = (id, idCampoJson, campo, valor) => {
    const atualizados = itens.map((item) =>
      item.id === id || (id == null && item.idCampoJson === idCampoJson)
        ? { ...item, [campo]: valor }
        : item
    );
    setItens(atualizados);
  };

  const handleConfirmSubmit = () => {
    const selecionados = itens.filter((item) => item.selecionado);
    if (selecionados.length === 0) return;

    onSubmit?.(selecionados);
    setToast({
      show: true,
      message: "Solicitação de adequação enviada com sucesso!",
      type: "success",
    });
    onClose();
    setShowConfirmModal(false);
  };

  if (isLoading) return <PageLoading minHeight="30vh" />;

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>SOLICITAÇÃO DE ADEQUAÇÃO</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Favor selecione os documentos que deseja solicitar adequação:</p>

          {itens.length === 0 && <p>Não há itens não conformes.</p>}

          {itens.map((item) => (
            <div key={item.id} className="mb-4 p-3 border rounded">
              <Form.Check
                type="checkbox"
                label={(item.idCampoJson ? titulosValor[item.idCampoJson] : undefined) || "Sem título"}
                checked={item.selecionado}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    item.idCampoJson,
                    "selecionado",
                    e.target.checked,
                  )
                }
              />

              <Form.Group className="mt-2">
                <Form.Label>Observação</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={item.observacao}
                  onChange={(e) =>
                    handleChange(
                      item.id,
                      item.idCampoJson,
                      "observacao",
                      e.target.value,
                    )
                  }
                  placeholder="Digite uma observação (opcional)"
                />
              </Form.Group>
            </div>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={onClose}
            className="px-4 py-2 fw-bold"
          >
            Cancelar
          </Button>

          {itens.length > 0 && (
            <Button
              onClick={() => {
                const selecionados = itens.filter((item) => item.selecionado);
                if (selecionados.length === 0) {
                  setToast({
                    show: true,
                    message:
                      "Selecione pelo menos um item para solicitar adequação.",
                    type: "error",
                  });
                  return;
                }

                onOpenConfirmModal(selecionados);
              }}
              variant="success"
              className="px-4 py-2 fw-bold"
            >
              Enviar Solicitação
            </Button>
          )}
        </Modal.Footer>

        <AdequacaoModalConfirmacao
          show={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            onClose();
          }}
          onConfirm={handleConfirmSubmit}
        />
      </Modal>

      {toast.show && (
        <NotificationToast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      )}
    </>
  );
};

export default SolicitacaoAdequacaoModal;
